import { isArray, isNil, AuctionStatuses } from 'vdlib';
import LookupsHelper from './LookupsHelper';
import Req from './RequestHelper';

const toServerRectangle = ({ x, y, width, height }) => ({
  X: x || 0,
  Y: y || 0,
  Width: width || 0,
  Height: height || 0,
});

const Api = {
  /* App */
  getInitialData: () => Req.postJson('app/initialdata', { ApplicationID: 2 }), //Cars
  login: credentials =>
    Req.anonymousPostJson('identity/authenticate', credentials),
  changeLanguage: locale =>
    Req.postJson('app/changelanguage', { Lang: locale }),

  changePassword: ({ oldPassword, newPassword }) =>
    Req.postJson('identity/changepassword', {
      OldPassword: oldPassword,
      NewPassword: newPassword,
    }),
  resetPassword: partyId =>
    Req.postJson('identity/resetpassword', { PartyId: partyId }),

  /* Dashboard */
  fetchDashboardData: () => Req.getJson('cars/app/dashboard/get'),
  readGDPRData: () => Req.getJson('cars/app/dashboard/gdpr'),

  /* GT Estimate */
  fetchGTEstimateData: payload =>
    Req.postJson('cars/gtestimations/getdashboarddata', {
      InsuranceID: payload.insuranceID,
    }),
  updateEstimations: payload =>
    Req.postJson('cars/gtestimations/updatepending', {
      DateFrom: payload.dateFrom,
      DateTo: payload.dateTo,
      InsuranceID: payload.insuranceId,
    }),
  resetProcessing: payload =>
    Req.postJson('cars/gtestimations/resetprocessing', {
      InsuranceID: payload.insuranceID,
    }),
  deleteEstimations: payload =>
    Req.postJson('cars/gtestimations/delete', {
      EstimationIDs: payload.estimationIDs,
    }),
  rerunGTEstimateData: payload =>
    Req.postJson('cars/gtestimations/rerun', {
      Estimations: payload.estimations,
      ForceRun: payload.forceRun,
      AllFailed: payload.allFailed,
      AllPending: payload.allPending,
      InsuranceID: payload.insuranceId,
      DateFrom: payload.dateFrom,
      DateTo: payload.dateTo,
    }),

  /* Fees */
  fetchMerchantFeesData: payload =>
    Req.postJson('cars/auctions/merchantfees', {
      MerchantID: payload.merchantID,
    }),
  updateMerchantFeesData: payload =>
    Req.postJson('cars/auctions/updatemerchantfees', {
      FeeIDs: payload.feeIDs,
      IsPaid: payload.isPaid,
    }),

  /* Auctions */
  fetchAuction: ({ auctionId }) =>
    Req.postJson('cars/auctions/querysummary', {
      AuctionId: parseInt(auctionId),
    }),
  fetchCurrentAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Active
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchNewAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Active
        ),
      ],
      OnlyNew: true,
      SkipStep: skipStep,
    }),
  fetchPendingApprovalAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Pending
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchForSelectionAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.ForSelection
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchForAcceptanceAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.ForAcceptance
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchCompletedAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Finished
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchLostAuctions: () => Req.postJson('cars/auctions/lost_querysummary'),
  fetchNoOfferAuctions: () => Req.postJson('cars/auctions/no_offer'),
  fetchRejectedAuctions: () =>
    Req.postJson('cars/auctions/rejected_querysummary'),
  fetchParticipateToAuctions: skipStep =>
    Req.postJson('cars/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Active
        ),
      ],
      IsParticipate: true,
      SkipStep: skipStep,
    }),
  fetchMerchantAuctionsSummary: () =>
    Req.postJson('cars/auctions/merchant_active_querysummary'),
  fetchOnlineAuctions: () => Req.postJson('cars/auctions/online'),

  uploadAuction: ({ insuranceId, file }) =>
    Req.uploadFile('upload/estimation/cars', file, {
      InsuranceID: insuranceId,
    }),
  createAuction: payload =>
    Req.postJson('cars/auctions/create', {
      Auction: payload.auctionData,
      Folder: payload.folderData,
    }),
  editAuction: payload =>
    Req.postJson('cars/auctions/edit', {
      Auction: payload.auctionData,
      Folder: payload.folderData,
    }),
  commentAuction: ({ auctionId, auctionStatusId, comments }) =>
    Req.postJson('cars/auctions/addcomments', {
      AuctionID: auctionId,
      AuctionStatusID: auctionStatusId,
      Comments: comments,
    }),

  startAuctions: ({ auctions, comments, useDefaultValues }) =>
    Req.postJson('cars/auctions/start', {
      IsDefault: useDefaultValues === true,
      Auctions: auctions,
      Comments: comments,
    }),
  endAuction: ({ bidId, ownerInfo }) =>
    Req.postJson('cars/auctions/end', { BidID: bidId, OwnerInfo: ownerInfo }),
  endAuctions: ({ auctionIds }) =>
    Req.postJson('cars/auctions/end_selected', {
      AuctionsIDs: auctionIds,
    }),
  acceptAuction: ({ auctionId, garageId }) =>
    Req.postJson('cars/auctions/accept', {
      AuctionID: auctionId,
      GarageID: garageId,
    }),
  rejectAuction: ({ auctionId, comments }) =>
    Req.postJson('cars/auctions/reject', {
      AuctionID: auctionId,
      Comments: comments,
    }),
  cancelAuctions: ({ auctionIds, reason }) =>
    Req.postJson('cars/auctions/cancel', {
      Auctions: (isArray(auctionIds) ? auctionIds : []).map(id => ({
        ID: id,
      })),
      Reason: reason,
    }),

  exportPdfAuctions: ({ auctionID }) =>
    Req.postJson('cars/reports/exportToPdfCarAuction', {
      AuctionID: auctionID,
    }),

  fetchCommentsForAuction: auctionId =>
    Req.postJson('cars/auctions/comments', { AuctionId: auctionId }),

  fetchPartsForAuction: auctionId =>
    Req.postJson('cars/auctions/parts', { AuctionId: auctionId }),

  fetchAuctionImages: auctionId =>
    Req.postJson('cars/auctions/images', { AuctionId: auctionId }),

  uploadAuctionImages: ({ auctionId: AuctionId, images }) =>
    Req.uploadFile('uploadmultiple/cars/auctions/images', images, {
      AuctionId,
    }),
  deleteAuctionImages: ({ auctionId: AuctionId, imageIds: ImageIds }) =>
    Req.postJson('cars/auctions/images/delete', {
      AuctionId,
      ImageIds,
    }),
  reorderAuctionImages: ({ auctionId: AuctionId, imageIds: OrderedImageIds }) =>
    Req.postJson('cars/auctions/images/reorder', {
      AuctionId,
      OrderedImageIds,
    }),
  removeImageLicensePlate: ({
    auctionId: AuctionId,
    imageId: ImageId,
    rectangle,
    container,
  }) =>
    Req.postJson('cars/auctions/images/remove-license-plate', {
      AuctionId,
      ImageId,
      Mask: toServerRectangle(rectangle),
      Container: toServerRectangle(container),
      RemoveMethod: window.VDGLOBALS.licenseRemoveMethod || 'blur',
    }),

  /* Bids */
  fetchBidsForAuction: auctionId =>
    Req.postJson('cars/bids/query', { AuctionId: auctionId, Statuses: [1, 2] }),
  createBids: ({ auctionId, bidData }) =>
    Req.postJson('cars/bids/create', {
      AuctionID: auctionId,
      Bids: isArray(bidData) ? bidData : [bidData],
    }),
  cancelBid: bidId => Req.postJson('cars/bids/cancel', { BidIds: [bidId] }),

  /* Parties */
  fetchParty: partyId =>
    Req.postJson('parties/query', { PartyId: parseInt(partyId) }),
  createParty: ({ PartyAppMemberships, RelatedPartyIDs, ...partyData }) =>
    Req.postJson('parties/create', {
      Party: partyData,
      PartyAppMemberships,
      RelatedPartyIDs: isArray(RelatedPartyIDs)
        ? RelatedPartyIDs
        : [RelatedPartyIDs],
    }),
  updateParty: ({ PartyAppMemberships, RelatedPartyIDs, ...partyData }) =>
    Req.postJson('parties/edit', {
      Party: partyData,
      PartyAppMemberships,
      RelatedPartyIDs: isArray(RelatedPartyIDs)
        ? RelatedPartyIDs
        : isNil(RelatedPartyIDs)
        ? []
        : [RelatedPartyIDs],
    }),
  deleteParty: partyId =>
    Req.postJson('parties/delete', {
      PartyId: partyId,
    }),
  enableDisableParty: partyId =>
    Req.postJson('parties/enable_disable', {
      PartyId: partyId,
    }),
  fetchPartiesData: () => Req.postJson('parties/query'),
};

export default Api;
